import React, { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Check, ChevronRight } from "lucide-react"
import { toast, Toaster } from "react-hot-toast"
import confetti from "canvas-confetti"
import { cn } from "../../utils/cn"
import PaymentCheckout from "../../components/career/payment-component"
import ProfileSetupComponent from "../../components/career/profile-setup-component"
import tokenService from "../../services/token-service"
import { apiRequest } from "../../services/api-client"
import { useAuth } from "../../context/auth-context"

const JobApplyForm = () => {
  const { jobId } = useParams()
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({})
  const [paymentProofFile, setPaymentProofFile] = useState(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const token = tokenService.getToken()
  const { refreshUser } = useAuth()

  // Load selected payment method from localStorage on mount
  useEffect(() => {
    const savedPaymentMethod = localStorage.getItem("selectedPaymentMethod")
    if (savedPaymentMethod) {
      setSelectedPaymentMethod(savedPaymentMethod)
    }
  }, [])

  const handleNextStep = (data) => {
    setFormData((prevData) => ({ ...prevData, ...data }))
    setCurrentStep((prevStep) => prevStep + 1)
  }

  const handlePrevStep = () => {
    setCurrentStep((prevStep) => prevStep - 1)
  }

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method)
    localStorage.setItem("selectedPaymentMethod", method)
  }

  const handleFileUpload = (file) => {
    setPaymentProofFile(file)
  }

  const handleNextClick = () => {
    if (currentStep === 2) {
      if (!selectedPaymentMethod) {
        toast.error("Please select a payment method before proceeding")
        return
      }
    }
    setCurrentStep((prevStep) => prevStep + 1)
  }

  const handleSubmit = async () => {
    if (!paymentProofFile) {
      toast.error("Please upload your payment receipt")
      return
    }

    setIsSubmitting(true)
    const formDataToSend = new FormData()

    // Append profile data
    Object.keys(formData).forEach((key) => {
      if (formData[key] instanceof File) {
        formDataToSend.append(key, formData[key], formData[key].name)
      } else {
        formDataToSend.append(key, formData[key])
      }
    })

    // Append payment proof
    formDataToSend.append("paymentProof", paymentProofFile)

    try {
      const headers = {
        Authorization: `Bearer ${token}`,
      }
      const response = await apiRequest("PUT", `/job-apply/${jobId}`, formDataToSend, headers)
      if (response.status === 201 || response.data) {
        localStorage.setItem("jobId", jobId)
        if (response.data?.user) {
          tokenService.setUserDetails(response.data.user)
        }
        if (refreshUser) {
          await refreshUser()
        }
        localStorage.removeItem("jobApplyFormData")
        localStorage.removeItem("selectedPaymentMethod")
        confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } })
        toast.success("Application submitted successfully!")
        setTimeout(() => navigate("/dashboard"), 2000)
      }
    } catch (error) {
      console.error("Job apply error:", error)
      toast.error(
        error.response?.data?.error?.message ||
          error.response?.data?.message ||
          "An error occurred while submitting your application. Please try again.",
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const steps = [
    {
      label: "Profile Setup",
      component: <ProfileSetupComponent onNext={handleNextStep} />,
    },
    {
      label: "Select Payment",
      component: (
        <PaymentCheckout
          step="select"
          onNext={handleNextStep}
          onSelectMethod={handlePaymentMethodSelect}
          selectedMethod={selectedPaymentMethod}
        />
      ),
    },
    {
      label: "Payment Details",
      component: <PaymentCheckout step="details" onNext={handleNextStep} selectedMethod={selectedPaymentMethod} />,
    },
    {
      label: "Upload Receipt",
      component: (
        <PaymentCheckout
          step="upload"
          onNext={handleSubmit}
          onFileUpload={handleFileUpload}
          isSubmitting={isSubmitting}
        />
      ),
    },
    { label: "Complete", component: <PaymentCheckout step="success" /> },
  ]

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <Toaster position="top-right" reverseOrder={false} />
      <div className="mx-auto max-w-4xl">
        {/* Stepper */}
        <div className="mb-8">
          <div className="flex items-center justify-center space-x-2">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                <StepIndicator step={index + 1} currentStep={currentStep} label={step.label} />
                {index < steps.length - 1 && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Current Step Component */}
        <div className="rounded-lg border bg-white shadow-sm">{steps[currentStep - 1].component}</div>

        {/* Navigation Buttons */}
        {currentStep > 1 && currentStep < 5 && (
          <div className="flex justify-end space-x-4 mt-6">
            <button
              onClick={handlePrevStep}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              disabled={isSubmitting}
            >
              Back
            </button>
            {currentStep < 4 && (
              <button
                onClick={handleNextClick}
                className="px-4 py-2 bg-[#F5A623] text-white rounded-md hover:bg-[#F5A623]/90"
              >
                Next
              </button>
            )}
            {currentStep === 4 && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !paymentProofFile}
                className="px-4 py-2 bg-[#F5A623] text-white rounded-md hover:bg-[#F5A623]/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  "Confirm Payment"
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const StepIndicator = ({ step, currentStep, label }) => {
  const isComplete = currentStep > step
  const isCurrent = currentStep === step

  return (
    <div className="flex flex-col items-center space-y-2">
      <div
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-medium",
          isComplete
            ? "border-[#F5A623] bg-[#F5A623] text-white"
            : isCurrent
              ? "border-[#F5A623] text-[#F5A623]"
              : "border-muted-foreground text-muted-foreground",
        )}
      >
        {isComplete ? <Check className="h-5 w-5" /> : step}
      </div>
      <span className={cn("text-sm", isCurrent ? "font-medium text-[#F5A623]" : "text-muted-foreground")}>
        {label}
      </span>
    </div>
  )
}

export default JobApplyForm